/**
 * Phone MY SCORES (DESIGN-CHANGELOG 2026-09-12 "Scoring is not a backlog
 * privilege · MY SCORES"; `design/GT Mobile Mine.dc.html`, the `isScores`
 * block).
 *
 * Full-screen behind MY GAMES' second door, the same bar as BACKLOG: `◂ MY
 * GAMES`, the title with the stat line under it — no `+`, because there is
 * nothing to add here; a score is put on a game where the game is. Then the
 * search box (`rtQ`), the filter rail, and a two-up grid sorted high → low.
 *
 * THE PHONE HAS NO HOVER, so each tile gets a permanent 44px footer row:
 * `WAS 6 · 2D AGO` on the left, `CHANGE` on the right. The score badge on the art opens the same YOUR SCORE modal.
 */

import { CoverArt } from '../components/CoverArt.tsx';
import { Ribbon } from '../components/Ribbon.tsx';
import { SearchBox } from '../components/SearchBox.tsx';
import { useArt, useQueueArt } from '../components/useArt.ts';
import { useQd } from '../store/index.ts';
import { removeChip } from '../store/selectors.3c.ts';
import { scoresVM } from '../store/selectors.ts';
import type { ScoreRow } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

function ScoreCard({ s: row }: { s: ScoreRow }): JSX.Element {
  const st = useQd();
  const art = useArt(row.id + 'p', row.coverCands);
  // `minWidth: 0` here and `minmax(0, 1fr)` in the grid: a `1fr` track will
  // not shrink below its content, so a long name would push the row off the
  // screen (the BACKLOG fix of 2026-09-04, carried over).
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, background: 'var(--gt-tile)', border: '1px solid var(--gt-line)' }}>
      <div
        onClick={() => st.openGame(row.id)}
        style={{ position: 'relative', width: '100%', aspectRatio: '2/3', background: row.fall, overflow: 'hidden', cursor: 'pointer' }}
      >
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={15} ini={row.ini} hasCover={art.hasCover} cover={art.cover} />
        <Ribbon up={row.upcoming} ea={row.ea} demo={row.demo} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,transparent 45%,rgba(5,5,8,.95) 85%)' }} />
        <div
          onClick={(ev) => {
            ev.stopPropagation();
            st.openScore(row.id);
          }}
          style={{
            cursor: 'pointer',
            position: 'absolute',
            top: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            background: 'var(--gt-ink)',
            borderLeft: '1px solid ' + row.scClr,
            borderBottom: '1px solid ' + row.scClr,
            padding: '7px 9px 6px',
            zIndex: 3,
          }}
        >
          <span style={{ fontFamily: PIXEL, fontSize: 15, color: row.scClr, display: 'flex', alignItems: 'center' }}>
            <span style={{ transform: 'translateY(0.13em)' }}>{row.scTxt}</span>
          </span>
          <span style={{ fontFamily: MONO, fontSize: 6.5, letterSpacing: '.12em', color: 'var(--gt-muted-2)' }}>MY SCORE</span>
        </div>
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '9px 10px', zIndex: 2 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 12,
              lineHeight: 1.25,
              color: '#fff',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.name}
          </div>
        </div>
      </div>
      {/* the phone has no hover, so the tile's actions are a permanent 44px row */}
      <div style={{ display: 'flex', alignItems: 'center', borderTop: '1px solid var(--gt-line)' }}>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            padding: '0 10px',
            fontFamily: MONO,
            fontSize: 8.5,
            letterSpacing: '.05em',
            color: 'var(--gt-text-5)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {row.when}
        </span>
        <div
          onClick={() => st.openScore(row.id)}
          data-tip={row.scTip}
          style={{
            height: 44,
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            padding: '0 12px',
            borderLeft: '1px solid var(--gt-line)',
            color: 'var(--acc)',
            fontFamily: MONO,
            fontSize: 9.5,
            letterSpacing: '.06em',
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          CHANGE
        </div>
      </div>
    </div>
  );
}

export default function Scores(): JSX.Element {
  const s = useQd();
  const v = scoresVM(s);

  // Same art fetch the shelf does (`screens/Backlog.tsx`): a game scored from
  // GAME DETAIL mid-session may be on no surface that fetched its assets.
  useQueueArt(v.rtRows.map((r) => r.id));

  return (
    <div
      data-screen-label="My scores"
      style={{ display: 'flex', flexDirection: 'column', flex: 1, paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* the bar: back · title + stat ----------------------------------------- */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: '11px 14px',
          borderBottom: '1px solid var(--gt-line)',
          background: 'var(--gt-panel)',
          position: 'sticky',
          top: 0,
          zIndex: 20,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            onClick={() => s.nav('mine')}
            style={{
              display: 'flex',
              gap: 7,
              alignItems: 'center',
              justifyContent: 'center',
              height: 44,
              padding: '0 11px',
              border: '1px solid var(--gt-border-2)',
              color: 'var(--gt-text-4)',
              fontFamily: MONO,
              fontSize: '9.5px',
              letterSpacing: '.05em',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flex: 'none',
              boxSizing: 'border-box',
            }}
          >
            ◂ MY GAMES
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
            <span style={{ fontFamily: PIXEL, fontSize: '9.5px', lineHeight: 1.6, color: 'var(--gt-text)', transform: 'translateY(0.13em)', whiteSpace: 'nowrap' }}>
              MY SCORES
            </span>
            <span
              style={{
                fontFamily: MONO,
                fontSize: '8.5px',
                letterSpacing: '.06em',
                color: 'var(--acc)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {v.rtSub}
            </span>
          </div>
        </div>
      </div>

      {/* search, then the filter rail ------------------------------------------ */}
      <div style={{ padding: '12px 14px 0' }}>
        <SearchBox
          value={v.rtQ}
          onChange={(e) => s.setRtQ(e.target.value)}
          onClear={() => s.setRtQ('')}
          placeholder="SEARCH WHAT YOU SCORED…"
          w="100%"
          ifs={12}
          pad="13px 12px"
        />
        {v.hasRtNote ? (
          <div style={{ marginTop: 8, fontFamily: MONO, fontSize: '9.5px', color: 'var(--gt-faint)' }}>{v.rtNote}</div>
        ) : null}
      </div>
      <div data-hs="1" style={{ display: 'flex', gap: 6, alignItems: 'center', overflowX: 'auto', padding: '10px 14px 0' }}>
        <div
          onClick={() => s.toggleFltOpen()}
          style={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid ' + v.fltBtnBd,
            color: v.fltBtnClr,
            background: v.fltBtnBg,
            fontFamily: MONO,
            fontSize: '10.5px',
            height: 40,
            padding: '0 13px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flex: 'none',
            boxSizing: 'border-box',
          }}
        >
          {v.fltBtnTxt}
        </div>
        {v.fltChips.map((c) => (
          <div
            key={c.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              border: '1px solid ' + c.bd,
              color: c.clr,
              background: c.bg,
              padding: '9px 11px',
              whiteSpace: 'nowrap',
              fontFamily: MONO,
              fontSize: 10,
              flex: 'none',
            }}
          >
            {c.l}
            <span onClick={() => removeChip(s, c)} style={{ cursor: 'pointer', color: 'var(--gt-text-5)', padding: 2 }}>
              ✕
            </span>
          </div>
        ))}
      </div>

      {v.rtNarrowed ? (
        <div style={{ padding: '40px 24px', textAlign: 'center', fontFamily: MONO, fontSize: 11, lineHeight: 2, color: 'var(--gt-muted)' }}>
          {v.rtNarrowTxt}
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12, padding: '14px 14px 26px' }}>
        {v.rtRows.map((row) => (
          <ScoreCard key={row.key} s={row} />
        ))}
      </div>

      {v.rtEmpty ? (
        <div style={{ padding: '0 14px 40px', textAlign: 'center', fontFamily: MONO, fontSize: 10, letterSpacing: '.06em', lineHeight: 1.8, color: 'var(--gt-muted)' }}>
          {v.rtEmptyTxt}
        </div>
      ) : null}
    </div>
  );
}
